import React, { useState } from 'react'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import TriggerDescription from '../TriggerDescription'
import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'
import Button from 'react-bootstrap/Button'
import { uniqueId } from 'lodash'

interface IProps {
  inputJson: any[]
  intent: any
  handleFulfillmentChange: (key: string, value: any) => void
}

const WebhookComponents: React.FC<IProps> = ({ inputJson, intent, handleFulfillmentChange }) => {
  const [details, setDetails] = useState<{ key: string; value: string }[]>([
    { key: 'details', value: '' }
  ])
  const [displayedValues, setDisplayedValues] = useState<string[]>([''])

  const handleActionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFulfillmentChange('action', e.target.value)
  }

  const handleDetailsKeyChange = (index: number, newValue: string) => {
    const newDetails = [...details]
    newDetails[index].key = newValue
    setDetails(newDetails)
    handleFulfillmentChange(
      'parameters',
      newDetails.reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {})
    )
  }

  const handleDetailsValueChange = (index: number, newValue: string) => {
    const newDetails = [...details]
    newDetails[index].value = `{previousValue:${newValue}}`
    setDetails(newDetails)

    // Update the displayed value
    const newDisplayedValues = [...displayedValues]
    newDisplayedValues[index] = newValue
    setDisplayedValues(newDisplayedValues)

    handleFulfillmentChange(
      'parameters',
      newDetails.reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {})
    )
  }

  const handleFallbackMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleFulfillmentChange('fallbackIntent', {
      ...intent.fulfillment.fallbackIntent,
      message: e.target.value
    })
  }

  const handleFallbackIntentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedIntentId = parseInt(e.target.value, 10)
    const selectedIntent = inputJson.find((item: any) => item.id === selectedIntentId)
    handleFulfillmentChange('fallbackIntent', {
      ...intent.fulfillment.fallbackIntent,
      intent: selectedIntent
    })
  }

  const handleAddDetails = () => {
    const newDetails = [...details, { key: `details_${details.length}`, value: '{previousValue:}' }]
    setDetails(newDetails)
    handleFulfillmentChange(
      'parameters',
      newDetails.reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {})
    )
  }

  const handleDeleteDetails = (index: number) => {
    // Check if it's the last remaining key:value pair
    if (details.length === 1) {
      return // Prevent deletion
    }

    const newDetails = [...details.slice(0, index), ...details.slice(index + 1)]
    setDetails(newDetails)
    handleFulfillmentChange(
      'parameters',
      newDetails.reduce((acc, cur) => ({ ...acc, [cur.key]: cur.value }), {})
    )
  }

  return (
    <div>
      <InputGroup className='my-3'>
        <InputGroup.Text>Action</InputGroup.Text>
        <Form.Control
          id={`action-${intent.id}`}
          type='text'
          name='action'
          defaultValue={intent.fulfillment?.action}
          onChange={handleActionChange}
        />
      </InputGroup>
      <label className='me-3 fs-6 fw-bold'>
        Parameters
        <Row>
          {details.map((detail, index) => (
            <React.Fragment key={uniqueId()}>
              <Col xs={8}>
                <InputGroup className='my-3'>
                  <Form.Control
                    className='bg-light'
                    type='text'
                    value={detail.key}
                    onChange={(e) => handleDetailsKeyChange(index, e.target.value)}
                  />
                  <Form.Select
                    id={`details-${intent.id}`}
                    className=''
                    name='details'
                    aria-label='Select Details'
                    value={displayedValues[index]}
                    onChange={(e) => handleDetailsValueChange(index, e.target.value)}
                  >
                    <option value=''>Select Value</option>
                    <TriggerDescription inputJson={inputJson} />
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col xs={2} className='mt-4'>
                <Button
                  type='button'
                  id='add-button'
                  variant='secondary'
                  className='d-flex'
                  size='sm'
                  onClick={handleAddDetails}
                >
                  <i className='bi bi-plus fs-3 p-0 m-0 text-primary' />
                </Button>
              </Col>
              <Col xs={2} className='mt-4'>
                <Button
                  type='button'
                  id={`delete-button-${index}`}
                  variant='secondary'
                  className='d-flex'
                  size='sm'
                  onClick={() => handleDeleteDetails(index)}
                >
                  <i className='bi bi-trash fs-3 p-0 m-0 text-danger' />
                </Button>
              </Col>
            </React.Fragment>
          ))}
        </Row>
      </label>
      <label className='me-3 fs-6 fw-bold'>
        Fallback Intent
        <InputGroup className='my-3'>
          <InputGroup.Text>Message</InputGroup.Text>
          <Form.Control
            id={`fallback-message-${intent.id}`}
            as='textarea'
            name='message'
            defaultValue={intent.fulfillment?.fallbackIntent?.message}
            onChange={handleFallbackMessageChange}
          />
        </InputGroup>
      </label>
      <InputGroup className='my-3'>
        <InputGroup.Text>Intent</InputGroup.Text>
        <Form.Select
          id={`fallback-intent-${intent.id}`}
          className=''
          name='intent'
          aria-label='Select Intent'
          defaultValue={intent.fulfillment?.fallbackIntent?.intent?.id || ''}
          onChange={handleFallbackIntentChange}
        >
          <option value=''>Select Intent</option>
          {inputJson.map((item: any) => (
            <option key={item.id} value={item.id}>
              {item.id}
            </option>
          ))}
        </Form.Select>
      </InputGroup>
    </div>
  )
}

export default WebhookComponents
